// ArticleDetail - Full article view

import React from 'react';
import {
  ChevronLeft,
  Calendar,
  User,
  Tag,
  Share2,
  BookOpen,
  ExternalLink,
  MapPin,
  Heart,
  Briefcase,
  Sparkles,
  Newspaper,
  Utensils
} from 'lucide-react';
import { type Article, ARTICLE_CATEGORIES } from '../../services/articlesService';
import { BluezoneRecipeEmbed } from './BluezoneRecipeEmbed';
import { Timestamp } from '@firebase/firestore';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

// Icon-Mapping für Kategorien
const CategoryIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "w-4 h-4" }) => {
  const icons: Record<string, React.ReactNode> = {
    'MapPin': <MapPin className={className} />,
    'Heart': <Heart className={className} />,
    'Briefcase': <Briefcase className={className} />,
    'Sparkles': <Sparkles className={className} />,
    'ExternalLink': <ExternalLink className={className} />,
    'Newspaper': <Newspaper className={className} />,
    'Utensils': <Utensils className={className} />,
  };
  return <>{icons[iconName] || null}</>;
};

interface ArticleDetailProps {
  article: Article;
  onBack: () => void;
}

export function ArticleDetail({ article, onBack }: ArticleDetailProps) {
  const formatDate = (date: Timestamp | Date | any) => {
    if (!date) return '';
    let d: Date;
    if (date instanceof Timestamp) {
      d = date.toDate();
    } else if (date.toDate && typeof date.toDate === 'function') {
      d = date.toDate();
    } else if (date.seconds) {
      // Handle serialized Firestore Timestamp (from JSON.parse)
      d = new Date(date.seconds * 1000);
    } else if (date instanceof Date) {
      d = date;
    } else {
      d = new Date(date);
    }
    if (isNaN(d.getTime())) return '';
    return format(d, 'd. MMMM yyyy', { locale: de });
  };

  const categoryInfo = ARTICLE_CATEGORIES.find((c) => c.value === article.category);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(window.location.href);
      alert('Link kopiert!');
    }
  };

  // Parse inline markdown formatting (bold, italic, links)
  const parseInlineFormatting = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let remaining = text;
    let keyIndex = 0;

    while (remaining.length > 0) {
      // Check for link [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        result.push(
          <a
            key={keyIndex++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-800 underline"
          >
            {linkMatch[1]}
          </a>
        );
        remaining = remaining.substring(linkMatch[0].length);
        continue;
      }

      // Check for bold **text**
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        result.push(
          <strong key={keyIndex++} className="font-semibold text-gray-900">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.substring(boldMatch[0].length);
        continue;
      }

      // Check for italic *text*
      const italicMatch = remaining.match(/^\*([^*]+)\*/);
      if (italicMatch) {
        result.push(
          <em key={keyIndex++} className="italic">
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.substring(italicMatch[0].length);
        continue;
      }

      // Find next special character or end of string
      const nextSpecial = remaining.search(/\[|\*/) ;
      if (nextSpecial === -1) {
        // No more special characters, add the rest as plain text
        result.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        // Special character at start but didn't match pattern, add it as text
        result.push(remaining[0]);
        remaining = remaining.substring(1);
      } else {
        // Add text before special character
        result.push(remaining.substring(0, nextSpecial));
        remaining = remaining.substring(nextSpecial);
      }
    }

    return result;
  };

  // Render content with full markdown support
  const renderContent = (content: string) => {
    // Split by double newlines for paragraphs
    const paragraphs = content.split(/\n\n+/);

    return paragraphs.map((paragraph, index) => {
      // Check for headings
      if (paragraph.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">
            {parseInlineFormatting(paragraph.substring(3))}
          </h2>
        );
      }
      if (paragraph.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
            {parseInlineFormatting(paragraph.substring(4))}
          </h3>
        );
      }

      // Check for bullet lists (starts with - or has lines starting with -)
      if (paragraph.startsWith('- ') || paragraph.includes('\n- ')) {
        const lines = paragraph.split('\n');
        return (
          <ul key={index} className="list-disc pl-6 space-y-2 my-4 text-gray-700">
            {lines.map((line, i) => {
              if (line.startsWith('- ')) {
                return <li key={i}>{parseInlineFormatting(line.substring(2))}</li>;
              }
              if (line.trim()) {
                return <p key={i}>{parseInlineFormatting(line)}</p>;
              }
              return null;
            })}
          </ul>
        );
      }

      // Check for blockquotes
      if (paragraph.startsWith('> ')) {
        return (
          <blockquote
            key={index}
            className="border-l-4 border-indigo-500 pl-4 py-2 my-6 italic text-gray-600 bg-gray-50 rounded-r-lg"
          >
            {parseInlineFormatting(paragraph.substring(2))}
          </blockquote>
        );
      }

      // Regular paragraph - also parse inline formatting
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-4">
          {parseInlineFormatting(paragraph)}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <span className="font-medium text-gray-900">Magazin</span>
            </div>
            <button
              onClick={handleShare}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Cover Image */}
      {article.coverImage && (
        <div className="w-full aspect-[21/9] bg-gray-100 overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Badge & External Link Indicator */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {categoryInfo && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
              <CategoryIcon iconName={categoryInfo.icon} className="w-4 h-4" />
              <span>{categoryInfo.label}</span>
            </span>
          )}
          {article.isExternal && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Externer Link</span>
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
          {article.title}
        </h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-6 leading-relaxed">
          {article.excerpt}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200 mb-8">
          <span className="flex items-center gap-1.5">
            <User className="w-4 h-4" />
            {article.author}
          </span>
          {article.publishedAt && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(article.publishedAt)}
            </span>
          )}
        </div>

        {/* External Link Button */}
        {article.isExternal && article.externalUrl && (
          <div className="mb-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-700 mb-3">
              Dieser Inhalt verlinkt zu einer externen Quelle.
            </p>
            <a
              href={article.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
            >
              <span>Zum Original</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {renderContent(article.content)}
        </div>

        {/* Bluezone Recipe Embed - nur fuer bluezone-Kategorie mit linkedRecipeId */}
        {article.category === 'bluezone' && (
          <>
            {article.linkedRecipeId ? (
              <BluezoneRecipeEmbed recipeId={article.linkedRecipeId} />
            ) : (
              <div className="my-8 bg-gray-100 rounded-2xl p-6 text-center text-gray-500">
                <p>Kein Rezept verknuepft</p>
              </div>
            )}
          </>
        )}

        {/* Tags */}
        {article.tags.length > 0 && (
          <div className="mt-12 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-gray-400" />
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium transition"
          >
            <ChevronLeft className="w-4 h-4" />
            Zurueck zum Magazin
          </button>
        </div>
      </article>
    </div>
  );
}

export default ArticleDetail;
